import * as React from 'react';

import { HeroPattern } from '@/components/hero-pattern';
import { StyledLink } from '@/components/styled-link';
import { Separator } from '@/components/ui/separator';
import {
  TypographyH1,
  TypographyList,
  TypographyP,
} from '@/components/ui/typography';

import {
  SectionHeading,
  TocLink,
} from '@/pages/unauthenticated/policies/components/headings';
import {
  Navigation,
  SectionFooter,
  SectionReadyToRelease,
  SignInOrDashboard,
} from '@/pages/unauthenticated/components';

import { tosToc } from './constants/toc';

export default function TermsOfServicePage() {
  return (
    <>
      <HeroPattern />
      <Navigation>
        <SignInOrDashboard />
      </Navigation>
      <main>
        <section className="redesign-container py-20 md:py-40">
          <div className="mx-auto max-w-7xl">
            <TypographyH1>Terms of Service</TypographyH1>
            <Separator className="my-4" />
            <TypographyList className="list-none">
              {tosToc.map((item) => {
                return (
                  <TocLink id={item.id} label={item.label} key={item.id} />
                );
              })}
            </TypographyList>
            <Separator className="my-4" />
            <SectionHeading id="overview">Overview</SectionHeading>
            <TypographyP>
              By visiting our site and/or purchasing something from FlagSync
              Inc., you engage in our &quot;Service&quot; and agree to be bound
              by the following terms and conditions (&quot;Terms of
              Service&quot;, &quot;Terms&quot;, &quot;Terms of Use&quot;).
            </TypographyP>
            <TypographyP>
              Please read these Terms of Service carefully before using the
              FlagSync Service offered by FlagSync Inc, a Massachusetts
              corporation. If you do not agree to all the terms and conditions
              of this agreement, then you may not access the website or use any
              FlagSync Services.
            </TypographyP>
            <SectionHeading id="intellectual-property">
              Definitions
            </SectionHeading>
            <TypographyP as="div">
              <div>
                <span className="font-medium">
                  &quot;FlagSync Service&quot;
                </span>{' '}
                refers to the FlagSync feature flag management and
                experimentation tool provided online to Customers through a
                subscription model.
              </div>
              <div>
                <span className="font-medium">&quot;Services&quot;</span> refers
                to the products and services offered by FlagSync Inc., which the
                Customer subscribes to, purchases through FlagSync&apos;s online
                portal, or receives at no cost under a free subscription tier,
                as applicable.
              </div>
            </TypographyP>
            <SectionHeading id="intellectual-property">
              Intellectual Property Rights
            </SectionHeading>
            <TypographyP>
              We are the owner or the licensee of all intellectual property
              rights of the FlagSync Service, including all source code,
              functionality, and software, and graphics in the Services
              (collectively, the &quot;Content&quot;), as well as the
              trademarks, service marks, and logos contained therein (the
              &quot;Marks&quot;). Our Content and Marks are protected by
              copyright and trademark laws (and various other intellectual
              property rights and unfair competition laws) and treaties in the
              United States and around the world.
            </TypographyP>
            <SectionHeading id="warranty">Warranty</SectionHeading>
            <TypographyP>
              The FlagSync Service is provided on an as-is and as-available
              basis. You agree that your use of the Services will be at your
              sole risk. To the fullest extent permitted by law, we disclaim all
              warranties, express or implied, in connection with the Services
              and your use thereof, including, without limitation, the implied
              warranties of merchantability, fitness for a particular purpose,
              and non-infringement.
            </TypographyP>
            <TypographyP>
              We make no warranties or representations about the accuracy or
              completeness of the Services&apos; content or the content of any
              websites or mobile applications linked to the Services and we will
              assume no liability or responsibility for any (1) errors,
              mistakes, or inaccuracies of content and materials, (2) personal
              injury or property damage, of any nature whatsoever, resulting
              from your access to and use of the Services, (3) any unauthorized
              access to or use of our secure servers and/or any and all personal
              information and/or financial information stored therein, (4) any
              interruption or cessation of transmission to or from the Services,
              (5) any bugs, and/or (6) any errors or omissions in any content
              and materials or for any loss or damage of any kind incurred as a
              result of the use of any content posted, transmitted, or otherwise
              made available via the Services.
            </TypographyP>
            <TypographyP>
              We do not warrant, endorse, guarantee, or assume responsibility
              for any Product or Service advertised or offered by a third party
              through the Services, any hyperlinked website, or any website or
              mobile application featured in any banner or other advertising,
              and we will not be a party to or in any way be responsible for
              monitoring any transaction between you and any third-party
              providers of Products or Services. As with the purchase of a
              product or Services through any medium or in any environment, you
              should use your best judgment and exercise caution where
              appropriate.
            </TypographyP>
            <SectionHeading id="indemnification">
              Indemnification
            </SectionHeading>
            <TypographyP>
              You agree to defend, indemnify, and hold us harmless, including
              our subsidiaries, affiliates, and all of our respective officers,
              agents, partners, and employees, from and against any loss,
              damage, liability, claim, or demand, including reasonable
              attorneys’ fees and expenses, made by any third party due to or
              arising out of: (1) use of the Services; (2) breach of these Legal
              Terms; (3) any breach of your representations and warranties set
              forth in these Legal Terms; (4) your violation of the rights of a
              third party, including but not limited to intellectual property
              rights; or (5) any overt harmful act toward any other user of the
              Services with whom you connected via the Services. Notwithstanding
              the foregoing, we reserve the right, at your expense, to assume
              the exclusive defense and control of any matter for which you are
              required to indemnify us, and you agree to cooperate, at your
              expense, with our defense of such claims. We will use reasonable
              efforts to notify you of any such claim, action, or proceeding
              which is subject to this indemnification upon becoming aware of
              it.
            </TypographyP>
            <SectionHeading id="subscriptions">Subscriptions</SectionHeading>
            <TypographyP>
              We work with a single payment processor,{' '}
              <StyledLink href="https://www.stripe.com">Stripe</StyledLink>, for
              subscription plans. Subscriptions may be canceled at any time,
              from the Organization Settings page within the Service. Once
              canceled, the subscription will remain active until the end of the
              previously purchased period, and will then expire without further
              renewal.
            </TypographyP>
            <SectionHeading id="user-data">User Data</SectionHeading>
            <TypographyP>
              We will maintain certain data that you transmit to the Services
              for the purpose of managing the performance of the Services, as
              well as data relating to your use of the Services. Although we
              perform regular routine backups of data, you are solely
              responsible for all data that you transmit or that relates to any
              activity you have undertaken using the Services. You agree that we
              shall have no liability to you for any loss or corruption of any
              such data, and you hereby waive any right of action against us
              arising from any such loss or corruption of such data.
            </TypographyP>
            <SectionHeading id="interruptions">
              Modifications and Interruptions
            </SectionHeading>
            <TypographyP>
              We reserve the right to change, modify, or remove the contents of
              the Services at any time or for any reason at our sole discretion
              without notice. However, we have no obligation to update any
              information on our Services. We will not be liable to you or any
              third party for any modification, price change, suspension, or
              discontinuance of the Services.
            </TypographyP>
            <TypographyP>
              We cannot guarantee the Services will be available at all times.
              We may experience hardware, software, or other problems related to
              upstream and/or downstream service, or need to perform maintenance
              related to the Services, resulting in interruptions, delays, or
              errors. We reserve the right to change, revise, update, suspend,
              discontinue, or otherwise modify the Services at any time or for
              any reason without notice to you. You agree that we have no
              liability whatsoever for any loss, damage, or inconvenience caused
              by your inability to access or use the Services during any
              downtime or discontinuance of the Services.
            </TypographyP>
            <SectionHeading id="termination">Termination</SectionHeading>
            <TypographyP>
              We reserve the right to, in our sole discretion and without notice
              or liability, deny access to and use of the Services (including
              blocking certain IP addresses), to any person for any reason or
              for no reason, including without limitation for breach of any
              representation, warranty, or covenant contained in these legal
              terms or of any applicable law or regulation.
            </TypographyP>
            <TypographyP>
              If we terminate or suspend your account for any reason, you are
              prohibited from registering and creating a new account under your
              name, a fake or borrowed name, or the name of any third party,
              even if you may be acting on behalf of the third party.
            </TypographyP>
            <SectionHeading id="governing-law">Governing Law</SectionHeading>
            <TypographyP>
              These Legal Terms shall be governed by and defined following the
              laws of Massachusetts. FlagSync and yourself irrevocably consent
              that the courts of Massachusetts shall have exclusive jurisdiction
              to resolve any dispute which may arise in connection with these
              Legal Terms.
            </TypographyP>
            <SectionHeading id="changes">
              Changes to This Privacy Policy
            </SectionHeading>
            <TypographyP>
              Any new features or tools which are added to FlagSync shall also
              be subject to the Terms of Service. You can review the most
              current version of the Terms of Service at any time on this page.
              We reserve the right to update, change or replace any part of
              these Terms of Service by posting updates and/or changes to our
              website. It is your responsibility to check this page periodically
              for changes. Your continued use of or access to the website
              following the posting of any changes constitutes acceptance of
              those changes.
            </TypographyP>
            <SectionHeading id="contact-us">Contact Us</SectionHeading>
            <TypographyP>
              In order to resolve a complaint regarding the Services or to
              receive further information regarding use of the Services, please
              contact us at{' '}
              <StyledLink href="mailto:support@flagsync.com">
                support@flagsync.com
              </StyledLink>
              .
            </TypographyP>
          </div>
        </section>
        <SectionReadyToRelease />
        <SectionFooter />
      </main>
    </>
  );
}
